import { api as generatedApi } from 'gen/rtk-openapi'

const TAG_TYPES = {
  invoices: 'Invoices',
  invoicePayments: 'InvoicePayments',
  outboundTickets: 'OutboundTickets',
  inboundTickets: 'InboundTickets',
  salesOrders: 'SalesOrders',
  bills: 'Bills',
}

export const api = generatedApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPES.invoices, TAG_TYPES.invoicePayments, TAG_TYPES.salesOrders, TAG_TYPES.outboundTickets],
  endpoints: {
    getInvoicesV2: {
      providesTags: result =>
        result && result.Items?.length > 0
          ? // successful query
            [
              ...result.Items.map(invoice => ({
                type: TAG_TYPES.invoices,
                id: invoice.id,
              })),
              { type: TAG_TYPES.invoices, id: 'LIST' },
            ]
          : // error occurred, refetch when `{ type: 'Invoices', id: 'LIST' }` is invalidated
            ([{ type: TAG_TYPES.invoices, id: 'LIST' }] as any),
    },
    getInvoiceV2: {
      providesTags: (_result, _error, { invoiceId }) => {
        const tags = [{ type: TAG_TYPES.invoices, id: invoiceId }]
        if (_result?.salesOrderId) {
          tags.push({ type: TAG_TYPES.salesOrders, id: _result.salesOrderId })
        }
        if (_result?.outboundTicketIds) {
          for (const outboundTicketId of _result.outboundTicketIds) {
            tags.push({ type: TAG_TYPES.outboundTickets, id: outboundTicketId })
          }
        }
        return tags
      },
    },
    getInvoiceV3: {
      providesTags: (_result, _error, { invoiceId }) => {
        const tags = [{ type: TAG_TYPES.invoices, id: invoiceId }]
        if (_result?.salesOrderId) {
          tags.push({ type: TAG_TYPES.salesOrders, id: _result.salesOrderId })
        }
        if (_result?.outboundTickets) {
          for (const outboundTicketData of _result.outboundTickets) {
            tags.push({ type: TAG_TYPES.outboundTickets, id: outboundTicketData.id })
          }
        }
        return tags
      },
    },
    createInvoiceV2: {
      invalidatesTags: [
        { type: TAG_TYPES.invoices, id: 'LIST' },
        { type: TAG_TYPES.outboundTickets, id: 'LIST' },
      ],
    },
    createInboundInvoiceV2: {
      invalidatesTags: [
        { type: TAG_TYPES.invoices, id: 'LIST' },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
      ],
    },
    updateInvoiceV2: {
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: TAG_TYPES.invoices, id: invoiceId },
        { type: TAG_TYPES.outboundTickets, id: 'LIST' },
      ],
    },
    updateInboundInvoiceV2: {
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: TAG_TYPES.invoices, id: invoiceId },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.inboundTickets, id: 'LIST' },
      ],
    },
    voidInvoice: {
      invalidatesTags: params => {
        const { invoiceId } = params as any
        return [
          { type: TAG_TYPES.invoices, id: invoiceId },
          { type: TAG_TYPES.invoices, id: 'LIST' },
          { type: TAG_TYPES.outboundTickets, id: 'LIST' },
        ]
      },
    },
    getInvoicePaymentByInvoiceId: {
      providesTags: (_result, _error, { invoiceId }) => [{ type: TAG_TYPES.invoicePayments, id: invoiceId }],
    },
    createInvoicePayment: {
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: TAG_TYPES.invoicePayments, id: invoiceId },
        { type: TAG_TYPES.invoicePayments, id: 'LIST' },
        { type: TAG_TYPES.invoices, id: invoiceId },
        { type: TAG_TYPES.invoices, id: 'LIST' },
      ],
    },
  },
})

export const {
  useGetInvoicesV2Query,
  useGetInvoiceV2Query,
  useGetInvoiceV3Query,
  useCreateInvoiceV2Mutation,
  useUpdateInvoiceV2Mutation,
  useVoidInvoiceMutation,
  useGetInvoicePaymentByInvoiceIdQuery,
  useCreateInvoicePaymentMutation,
  useCreateInboundInvoiceV2Mutation,
  useUpdateInboundInvoiceV2Mutation,
} = api
